import { apiGet } from './api';
import type {
  WorkPackageType,
  Status,
  Priority,
  HalCollection,
  CustomField,
  WorkPackageSchema,
} from '@/types';

// Cache pentru date care nu se schimbă frecvent
let typesCache: WorkPackageType[] | null = null;
let statusesCache: Status[] | null = null;
let prioritiesCache: Priority[] | null = null;

// Obținere toate tipurile de work packages
export async function getTypes(forceRefresh = false): Promise<WorkPackageType[]> {
  if (typesCache && !forceRefresh) {
    return typesCache;
  }

  const response = await apiGet<HalCollection<WorkPackageType>>('/types');
  typesCache = response._embedded.elements;
  return typesCache;
}

// Obținere tip după ID
export async function getType(typeId: number): Promise<WorkPackageType> {
  return apiGet<WorkPackageType>(`/types/${typeId}`);
}

// Obținere toate statusurile
export async function getStatuses(forceRefresh = false): Promise<Status[]> {
  if (statusesCache && !forceRefresh) {
    return statusesCache;
  }

  const response = await apiGet<HalCollection<Status>>('/statuses');
  statusesCache = response._embedded.elements;
  return statusesCache;
}

// Obținere status după ID
export async function getStatus(statusId: number): Promise<Status> {
  return apiGet<Status>(`/statuses/${statusId}`);
}

// Obținere toate prioritățile
export async function getPriorities(forceRefresh = false): Promise<Priority[]> {
  if (prioritiesCache && !forceRefresh) {
    return prioritiesCache;
  }

  const response = await apiGet<HalCollection<Priority>>('/priorities');
  prioritiesCache = response._embedded.elements;
  return prioritiesCache;
}

// Obținere prioritate după ID
export async function getPriority(priorityId: number): Promise<Priority> {
  return apiGet<Priority>(`/priorities/${priorityId}`);
}

// Resetare cache (de exemplu la logout)
export function clearMetadataCache(): void {
  typesCache = null;
  statusesCache = null;
  prioritiesCache = null;
}

// Obținere schema completă pentru work packages (include custom fields)
export async function getWorkPackageFormSchema(
  projectId: number,
  typeId?: number
): Promise<WorkPackageSchema> {
  const payload: Record<string, unknown> = {};

  if (typeId) {
    payload._links = {
      type: { href: `/api/v3/types/${typeId}` },
    };
  }

  const response = await apiGet<{
    _embedded: { schema: WorkPackageSchema };
  }>(`/projects/${projectId}/work_packages/form`, {
    params: payload,
  });

  return response._embedded.schema;
}

// Extragere custom fields din schema
export function extractCustomFieldsFromSchema(
  schema: WorkPackageSchema
): CustomField[] {
  const customFields: CustomField[] = [];

  Object.entries(schema).forEach(([key, value]) => {
    // Custom fields încep de obicei cu "customField"
    if (key.startsWith('customField') && typeof value === 'object' && value !== null) {
      const field = value as {
        type: string;
        name: string;
        required: boolean;
        hasDefault: boolean;
        writable: boolean;
        _embedded?: {
          allowedValues?: Array<{ id: number; name: string }>;
        };
      };

      const idMatch = key.match(/customField(\d+)/);
      const fieldId = idMatch ? parseInt(idMatch[1], 10) : 0;

      customFields.push({
        id: fieldId,
        name: field.name,
        fieldFormat: field.type,
        required: field.required,
        isForAll: true,
        position: 0,
        possibleValues: field._embedded?.allowedValues?.map((v) => v.name),
      });
    }
  });

  return customFields;
}

// Găsire tip după nume (case-insensitive)
export async function findTypeByName(name: string): Promise<WorkPackageType | null> {
  const types = await getTypes();
  const normalizedName = name.toLowerCase();

  return (
    types.find(
      (t) =>
        t.name.toLowerCase() === normalizedName ||
        t.name.toLowerCase().includes(normalizedName)
    ) || null
  );
}

// Găsire status după nume
export async function findStatusByName(name: string): Promise<Status | null> {
  const statuses = await getStatuses();
  const normalizedName = name.toLowerCase();

  return (
    statuses.find(
      (s) =>
        s.name.toLowerCase() === normalizedName ||
        s.name.toLowerCase().includes(normalizedName)
    ) || null
  );
}

// Găsire prioritate după nume
export async function findPriorityByName(name: string): Promise<Priority | null> {
  const priorities = await getPriorities();
  const normalizedName = name.toLowerCase();

  return (
    priorities.find(
      (p) =>
        p.name.toLowerCase() === normalizedName ||
        p.name.toLowerCase().includes(normalizedName)
    ) || null
  );
}

// Grupare statusuri pe categorii (pentru dashboard)
export interface StatusGroups {
  todo: Status[];
  inProgress: Status[];
  completed: Status[];
  cancelled: Status[];
}

export async function getStatusGroups(): Promise<StatusGroups> {
  const statuses = await getStatuses();

  const groups: StatusGroups = {
    todo: [],
    inProgress: [],
    completed: [],
    cancelled: [],
  };

  statuses.forEach((status) => {
    // fold diacritice românești (ș/ş/ț/ţ/ă/â/î) ca potrivirea să fie insensibilă la variante
    const name = (status.name || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '');
    const isRejected = /(respin|anulat|refuz|renunt|abandon)/.test(name);
    if (status.isClosed) {
      if (isRejected) groups.cancelled.push(status);   // NOU: „Respinse"
      else groups.completed.push(status);
    } else if (/(progres|development|dezvolt|lucru|curs|testar|testat|testing|verific|review|asteptare|implement)/.test(name)) {
      groups.inProgress.push(status);
    } else {
      groups.todo.push(status);
    }
  });

  return groups;
}

// Identificare dacă un tip este Phase
export function isPhaseType(type: WorkPackageType): boolean {
  const name = type.name.toLowerCase();
  return (
    name.includes('phase') ||
    name.includes('fază') ||
    name.includes('faza')
  );
}

// Identificare dacă un tip este Task
export function isTaskType(type: WorkPackageType): boolean {
  const name = type.name.toLowerCase();
  return (
    name.includes('task') ||
    name.includes('activitate') ||
    name.includes('activitatea')
  );
}

// Identificare dacă un tip este Milestone
export function isMilestoneType(type: WorkPackageType): boolean {
  return type.isMilestone || type.name.toLowerCase().includes('milestone');
}
